import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LuxSolutionsLogo, LuxSolutionsMark } from "@/components/ProjectInquiry";
import { LayoutGrid, MessageSquareText, Wrench, LogOut, Lightbulb, KeyRound, BarChart3, BookMarked, CircleDollarSign } from "lucide-react";

const links = [
  { to: "/dashboard", label: "Kurs", icon: LayoutGrid },
  { to: "/coach", label: "KI-Coach", icon: MessageSquareText },
  { to: "/tools", label: "Werkzeuge", icon: Wrench },
  { to: "/notizen", label: "Notizen", icon: BookMarked },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 glass-strong border-b hairline">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center">
          <Link to="/dashboard" className="flex items-center gap-2.5 group" data-testid="navbar-logo">
            <div className="relative">
              <Lightbulb className="h-6 w-6 text-gold transition-transform group-hover:scale-110" />
              <div className="absolute inset-0 blur-md bg-gold/40 rounded-full -z-10 animate-pulse-glow" />
            </div>
            <div className="leading-none">
              <span className="font-heading font-extrabold text-lg tracking-tight">LUX</span>
              <span className="block text-[9px] uppercase tracking-[0.25em] text-muted-foreground">Lighting Academy</span>
            </div>
          </Link>
          <span className="hidden md:flex items-center gap-2 ml-3 pl-3 border-l hairline">
            <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">by</span>
            <LuxSolutionsLogo imgClassName="h-10 w-auto" />
          </span>
          <span className="flex md:hidden items-center ml-2.5 pl-2.5 border-l hairline">
            <LuxSolutionsMark imgClassName="h-7 w-auto rounded-md" />
          </span>
        </div>

        <nav className="flex items-center gap-1">
          {links.map((l) => {
            const active = location.pathname.startsWith(l.to);
            const Icon = l.icon;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`navlink-${l.label.toLowerCase()}`}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                  active
                    ? "bg-gold/15 text-gold border border-gold/30"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent"
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{l.label}</span>
              </Link>
            );
          })}
          {user?.role === "admin" && (
            <Link
              to="/admin/stats"
              data-testid="navlink-stats"
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                location.pathname.startsWith("/admin/stats")
                  ? "bg-gold/15 text-gold border border-gold/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent"
              }`}
            >
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Statistik</span>
            </Link>
          )}
          {user?.role === "admin" && (
            <Link
              to="/admin/licenses"
              data-testid="navlink-admin"
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                location.pathname.startsWith("/admin/licenses")
                  ? "bg-gold/15 text-gold border border-gold/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent"
              }`}
            >
              <KeyRound className="h-4 w-4" />
              <span className="hidden sm:inline">Admin</span>
            </Link>
          )}
          {user?.role === "admin" && (
            <Link
              to="/admin/pricing"
              data-testid="navlink-pricing"
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                location.pathname.startsWith("/admin/pricing")
                  ? "bg-gold/15 text-gold border border-gold/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent"
              }`}
            >
              <CircleDollarSign className="h-4 w-4" />
              <span className="hidden sm:inline">Preise</span>
            </Link>
          )}
          <div className="ml-2 sm:ml-4 flex items-center gap-3 pl-2 sm:pl-4 border-l hairline">
            <div className="hidden md:flex flex-col items-end leading-none">
              <span className="text-sm font-medium">{user?.name}</span>
              <span className="text-[10px] text-muted-foreground">{user?.email}</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="h-9 w-9 flex items-center justify-center rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
              title="Abmelden"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
}
